import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getUniqId, LocalStorageService } from '@mm/utils';
import {
  selectActiveCompanyId,
  activeChatbotIdSelector,
  activeCompanySelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { Box } from '@mm/components';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import {
  ArrowStatefulIcon,
  ExclamationIcon,
  ExclamationTriangleSolidIcon,
} from '@mm/icons';
import './FacebookPageErrors.pcss';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import {
  ERROR_TYPES,
  ErrorNumber,
  MESSAGES_EXCEPTION_ACTION,
  TEMPORARY_ISSUE_HEADER,
  IGNORED_ERROR_TYPES,
  DissmissedErrorType,
} from './constants';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectFacebookPageErrors } from '@mm/state/selectors/errorsSelector';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { randomId } from '../../helpers/utils';
import { XRaySlideoutBanner } from '@mm/icons/SlideoutBanners';
import { FacebookErrorModel } from '@mm/state/model/FacebookErrorModel';
import { useGetIsDissmissedFailedPaymentError } from '../../modals/FailedPaymentModal/FailedPaymentModal';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

const RenderHTML = props => (
  <span dangerouslySetInnerHTML={{ __html: props.HTML }} />
);

export type ErrorActionComponentProps = {
  pagination: number;
  companyId: number;
  pageId: number;
  error: LocalErrorType | FacebookErrorModel;
  errorId: number;
  chatbotId: number;
  onClick: () => void;
  deleteFacebookError: (
    pageId: number,
    errorId: number,
    ignore?: boolean,
  ) => void;
  getConnectedPages: (pageId: number, pageNumber: number) => void;
  getConnectedPage: (pageId: number) => void;
  onDismissClick?: (errorId: ErrorNumber) => void;
};

export type LocalErrorType = {
  id: string;
  errorType: DissmissedErrorType;
  details: {
    number: number;
  };
  severity: 'info' | 'warning' | 'error' | 'fatal';
  hide: boolean;
  guide?: JSX.Element;
  guideTitle?: string;
  onDismissClick?: () => void;
};

const MM_DISSMISSED_ERRORS = 'mmDismissedErrors';

export const FacebookPageErrors = () => {
  const companyId = useSelector(selectActiveCompanyId);
  const page = useSelector(selectActiveFacebookPage);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const hasAutomationsFlag = featureFlags.automations;
  const hasKlaviyoCompliantFlag = featureFlags.compliant_klaviyo_app;

  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();
  const { isFailedPaymentErrorDismissed, dismissFailedPaymentError } =
    useGetIsDissmissedFailedPaymentError();
  const facebookPage = useSelector(selectActiveFacebookPage);
  const company = useSelector(activeCompanySelector);

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const [guideOpen, setGuideOpen] = useState(false);

  const isShopify = useSelector(selectIsShopifyContext);

  const retrieveDefaultQueries = () =>
    JSONUtils.safeParse<{ [key: string]: string[] }>(
      LocalStorageService.getItem(MM_DISSMISSED_ERRORS),
    ) || {};

  const defaultDissmissedErrors = retrieveDefaultQueries();

  const getIsErrorDissmissed = (type: DissmissedErrorType) => {
    if (facebookPage) {
      return !!defaultDissmissedErrors[facebookPage.id]?.find(
        item => item === type,
      );
    }
    return false;
  };

  const facebookErrors =
    useSelector(selectFacebookPageErrors)?.filter(
      error =>
        !IGNORED_ERROR_TYPES.find(
          item =>
            item.code === error?.code &&
            item.subCode === error?.subCode &&
            item.number === error?.details?.number,
        ),
    ) || [];

  const LOCAL_ERRORS = [
    {
      id: randomId(),
      errorType: 'xRayNotInstalledException',
      details: {
        number: 90,
      },
      severity: 'warning',
      hide:
        facebookPage?.getemailsActivated ||
        getIsErrorDissmissed('xRayNotInstalledException') ||
        !hasAutomationsFlag,
      guide: (
        <XRaySlideoutBanner
          textColor="normal"
          className="facebook-page-errors__guide-guide-image"
        />
      ),
      guideTitle: `How ${companyName} website visitor works`,
    },
    {
      id: randomId(),
      errorType: 'missingBillingAddress',
      details: {
        number: 92,
      },
      severity: 'warning',
      hide:
        !company?.requiresAddressVerification ||
        getIsErrorDissmissed('missingBillingAddress'),
    },
    {
      id: randomId(),
      errorType: 'emailNotConnectedException',
      details: {
        number: 91,
      },
      severity: 'warning',
      hide:
        isShopify ||
        facebookPage?.emailActivated ||
        getIsErrorDissmissed('emailNotConnectedException') ||
        !hasAutomationsFlag ||
        hasKlaviyoCompliantFlag,
    },
    {
      id: randomId(),
      errorType: 'failedPayment',
      details: {
        number: 93,
      },
      severity: 'error',
      hide:
        !facebookPage?.subscription?.failedPayment ||
        isFailedPaymentErrorDismissed,
      onDismissClick: () => {
        dismissFailedPaymentError();
      },
    },
    {
      id: randomId(),
      errorType: 'xrayLimiReached',
      details: {
        number: 94,
      },
      severity: 'warning',
      hide:
        !facebookPage?.xrayLockedAt || getIsErrorDissmissed('xrayLimiReached'),
      onDismissClick: () => {
        dismissFailedPaymentError();
      },
    },
    {
      id: randomId(),
      errorType: 'budgetCapLimitReached',
      details: {
        number: 95,
      },
      severity: 'warning',
      hide:
        !facebookPage?.budgetCapReachedAt ||
        getIsErrorDissmissed('budgetCapLimitReached'),
      onDismissClick: () => {
        dismissFailedPaymentError();
      },
    },
  ].filter(error => !error.hide) as LocalErrorType[];

  const [errors, setErrors] = useState([...LOCAL_ERRORS]);
  const [activeIndex, setActiveIndex] = useState(0);

  const getNextIndex = () =>
    activeIndex === (errors?.length || 0) - 1 ? 0 : activeIndex + 1;

  const getPrevIndex = () =>
    activeIndex === 0 ? (errors?.length || 0) - 1 : activeIndex - 1;

  const next = () => {
    setActiveIndex(getNextIndex());
  };

  const previous = () => {
    setActiveIndex(getPrevIndex());
  };

  const getFacebookError = facebookError => {
    const fbCode = facebookError?.code;
    const fbSubCode = facebookError?.subCode;
    const errorDetailsNumber = facebookError?.details?.number;

    const fbError =
      errorDetailsNumber === ErrorNumber.IGMessagingException && fbCode
        ? ERROR_TYPES[errorDetailsNumber][fbCode]
        : errorDetailsNumber === ErrorNumber.FBMessagingException &&
          fbCode &&
          fbSubCode &&
          facebookError?.errorType !== 'messages_blocked_exception'
        ? ERROR_TYPES[errorDetailsNumber][fbCode][fbSubCode]
        : ERROR_TYPES[errorDetailsNumber];

    return fbError;
  };

  const getConnectedPage = (
    ...args: Parameters<typeof connectedPageCRUDActions.retrievePage>
  ) => {
    connectedPageCRUDActions.retrievePage(...args);
  };

  const getConnectedPages = (companyId: number, pagination) => {
    connectedPageCRUDActions.retrieveAllByCompany(companyId, pagination);
  };

  const deleteFacebookError = (
    pageId: number,
    errorId: number,
    ignore: boolean,
  ) => {
    facebookErrorCRUDActions.delete(pageId, errorId, ignore);
  };

  const isFacebookError = (
    error: LocalErrorType | FacebookErrorModel,
  ): error is FacebookErrorModel =>
    !!(error as FacebookErrorModel).originalMessage;

  const getTypeByErrorId = (errorNum?: ErrorNumber) =>
    LOCAL_ERRORS.find(err => err.details.number === errorNum)?.errorType;

  const onDismissClick = (errorNum?: ErrorNumber, onDismiss?: () => void) => {
    const type = getTypeByErrorId(errorNum);

    if (facebookPage && type) {
      const updatedErrors = {
        ...defaultDissmissedErrors,
        [facebookPage.id]: [
          ...(defaultDissmissedErrors[facebookPage.id] || []),
          type,
        ],
      };

      setErrors(stateErrors => [
        ...stateErrors.filter(error => error?.errorType !== type),
      ]);
      if (!getIsErrorDissmissed(type)) {
        LocalStorageService.setItem(
          MM_DISSMISSED_ERRORS,
          JSON.stringify(updatedErrors),
        );
      }

      onDismiss?.();
    }
  };

  useEffect(() => {
    setActiveIndex(0);
  }, [errors?.length]);

  useEffect(() => {
    setErrors([...LOCAL_ERRORS]);
  }, [
    facebookErrors.length,
    facebookPage?.getemailsActivated,
    facebookPage?.onceSuccessfullyLoadedGlobalJs,
  ]);

  if (!page || !errors || (errors && !errors.length)) {
    return null;
  }

  const renderBody = (facebookError, fbError) => {
    if (fbError) {
      const body =
        typeof fbError.body === 'function'
          ? fbError.body(facebookError)
          : fbError.body;
      return body;
    }
    if (isFacebookError(facebookError)) {
      return (
        <RenderHTML
          HTML={facebookError.details.message || facebookError?.originalMessage}
          key={facebookError.id}
        />
      );
    }
    return null;
  };

  return (
    <div className="facebook-page-errors__wrapper">
      <Box
        className={classNames('facebook-page-errors__box', {
          oldStyle: !hasAutomationsFlag,
          warning:
            errors[activeIndex]?.severity === 'warning' ||
            errors[activeIndex]?.severity === 'info',
        })}
      >
        {errors.length > 1 && (
          <div className="facebook-page-errors__pagination">
            <PrimaryButton
              onClick={previous}
              className="facebook-page-errors__pagination-button"
              disabled={activeIndex === 0}
            >
              <ArrowStatefulIcon
                direction="left"
                size={15}
                color={activeIndex !== 0 ? '#262626' : '#BFBFBF'}
              />
            </PrimaryButton>
            <div className="facebook-page-errors__pagination-qty">
              {' '}
              {activeIndex + 1}/ {errors.length}
            </div>
            <PrimaryButton
              onClick={next}
              className="facebook-page-errors__pagination-button"
              disabled={getNextIndex() < activeIndex}
            >
              <ArrowStatefulIcon
                direction="right"
                size={15}
                color={getNextIndex() > activeIndex ? '#262626' : '#BFBFBF'}
              />
            </PrimaryButton>
          </div>
        )}
        {errors?.map((facebookError, index) => {
          const fbError = getFacebookError(facebookError);

          let header = fbError ? fbError.header : TEMPORARY_ISSUE_HEADER;
          if (typeof header === 'function') {
            header = header(facebookError);
          }

          const ActionComponent = fbError
            ? fbError.action
            : MESSAGES_EXCEPTION_ACTION;

          if (activeIndex !== index) {
            return <React.Fragment key={getUniqId('error')} />;
          }

          return (
            <div
              key={getUniqId('error')}
              className="facebook-page-errors__container"
            >
              <div className="facebook-page-errors__description">
                <div className="facebook-page-errors__icon">
                  {facebookError?.severity === 'fatal' ||
                  facebookError?.severity === 'error' ? (
                    <ExclamationTriangleSolidIcon
                      className="facebook-page-errors__tab-icon"
                      color="#CF1322"
                    />
                  ) : (
                    <ExclamationIcon
                      className="facebook-page-errors__tab-icon"
                      color="#FA8C16"
                    />
                  )}
                </div>
                <div
                  key={facebookError.id}
                  className="facebook-page-errors__body-wrapper"
                >
                  {header}
                  {renderBody(facebookError, fbError)}
                  <div className="facebook-page-errors__actions-wrapper">
                    <ActionComponent
                      chatbotId={chatbotId}
                      companyId={companyId}
                      pageId={page.id}
                      deleteFacebookError={deleteFacebookError}
                      error={facebookError}
                      errorId={facebookError.id}
                      getConnectedPage={getConnectedPage}
                      getConnectedPages={getConnectedPages}
                      onDismissClick={onDismissClick}
                    />
                  </div>
                </div>
              </div>

              {onDismissClick && (
                <span
                  className="facebook-page-errors__close"
                  onClick={() =>
                    onDismissClick(
                      facebookError?.details?.number,
                      (facebookError as LocalErrorType)?.onDismissClick,
                    )
                  }
                >
                  <CloseOutlined />
                </span>
              )}
            </div>
          );
        })}
      </Box>
      {(errors[activeIndex] as LocalErrorType)?.guide && (
        <div
          className={classNames('facebook-page-errors__guide-wrapper', {
            'facebook-page-errors__guide-wrapper-with-pagination':
              errors.length > 1,
          })}
          onClick={() => setGuideOpen(!guideOpen)}
        >
          <div className="facebook-page-errors__guide-title">
            {guideOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}Guide:
            {(errors[activeIndex] as LocalErrorType)?.guideTitle}
          </div>
          {guideOpen && (
            <div className="facebook-page-errors__guide-body">
              {(errors[activeIndex] as LocalErrorType)?.guide}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
